import React, { useRef, useEffect } from "react";
import DOMPurify from "dompurify";
import ReactMarkdown from "react-markdown";
import gfm from "remark-gfm"; // For GitHub Flavored Markdown
import AssistantStyles from "../assets/stylesheets/Assistant.module.scss";
import { HiOutlineClipboard } from "react-icons/hi";
import hljs from "highlight.js";
import "highlight.js/styles/default.css"; // Import a code style from highlight.js
import toast from "react-hot-toast";

export const cleanStyleString = (styleStr: string): string => {
  if (!styleStr) return "";
  const parts = styleStr.split(';');
  const allowedProperties = [
    'width', 'height', 'max-width', 'max-height', 'min-width', 'min-height',
    'margin', 'margin-left', 'margin-right', 'margin-top', 'margin-bottom',
    'padding', 'padding-left', 'padding-right', 'padding-top', 'padding-bottom',
    'float', 'display', 'text-align', 'color', 'background-color'
  ];
  
  const cleanParts = parts.map(part => {
    const trimmed = part.trim();
    if (!trimmed) return '';
    const colonIdx = trimmed.indexOf(':');
    if (colonIdx === -1) return '';
    const prop = trimmed.substring(0, colonIdx).trim().toLowerCase();
    const val = trimmed.substring(colonIdx + 1).trim().toLowerCase();
    
    if (allowedProperties.includes(prop)) {
      // Block viewport relative units to prevent viewport hijacking overlays
      if (val.includes('vw') || val.includes('vh') || val.includes('vmin') || val.includes('vmax')) {
        return '';
      }
      return `${prop}: ${val}`;
    }
    return '';
  }).filter(Boolean);
  
  return cleanParts.join('; ');
};

/**
 * Strictly sanitizes HTML to prevent XSS. 
 * Especially important since researchers may use malicious payloads.
 */
export const sanitizeHtml = (html: string) => {
  const cleanHtml = DOMPurify.sanitize(html, {
    // Explicitly allow safe tags
    ALLOWED_TAGS: [
      'p', 'br', 'b', 'i', 'strong', 'em', 'strike', 'u', 'sub', 'sup',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'task-list', 'task-item',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'a', 'img', 'div', 'span', 'code', 'pre', 'hr',
      'blockquote', 'mark'
    ],
    // Explicitly allow safe attributes
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title', 'class', 'style', 'target', 'rel',
      'width', 'height', 'align', 'colspan', 'rowspan', 'data-type', 'checked'
    ],
    // Block dangerous protocols
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    // Add security for links
    ADD_ATTR: ['target', 'rel'],
    FORBID_TAGS: ['script', 'style', 'iframe', 'frame', 'object', 'embed', 'base', 'meta', 'link'],
    FORBID_ATTR: ['onerror', 'onload', 'onmouseover', 'onfocus', 'onclick', 'action', 'formaction'],
    RETURN_DOM_FRAGMENT: false,
    RETURN_DOM: false,
  });

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(cleanHtml, 'text/html');
    doc.querySelectorAll('[style]').forEach(el => {
      const style = el.getAttribute('style') || '';
      el.setAttribute('style', cleanStyleString(style));
    });
    // Remove custom styling wrappers if present
    doc.querySelectorAll('img[containerstyle], img[wrapperstyle]').forEach(img => {
      img.removeAttribute('containerstyle');
      img.removeAttribute('wrapperstyle');
    });
    return { __html: doc.body.innerHTML };
  } catch (e) {
    return { __html: cleanHtml };
  }
};

interface MarkdownViewerProps {
  data: string;
  languageUsed: string;
}

// const CodeBlock: React.ElementType = ({ children }) => {
//   return <pre className={AssistantStyles.codeBlock}>{children}</pre>;
// };

const CodeBlock: React.ElementType = ({ children, languageUsed }) => {
  console.log(languageUsed, "ll");
  const codeRef = useRef(null);
  // Function to copy the code to the clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(children.toString());
    toast("Copied to clipboard", {
      icon: "📎",
      duration: 1000
    });
    // You can also add a notification or tooltip to indicate that the code has been copied.
  };

  useEffect(() => {
    if (codeRef.current) {
      // Highlight the code block with auto-detection
      hljs.highlightBlock(codeRef.current);
    }
  }, [languageUsed]);

  return (
    <div className={`${AssistantStyles.codeBlock}`}>
      <div className={AssistantStyles.copyBtnContainer}>
        <button
          className={`${AssistantStyles.copyButton}`}
          onClick={copyToClipboard}
        >
          <HiOutlineClipboard className="mr-1" />
          Copy
        </button>
      </div>
      <pre>
        <code className={`language-${languageUsed}`} ref={codeRef}>
          {children}
        </code>
      </pre>
    </div>
  );
};

export const MarkdownViewer: React.FC<MarkdownViewerProps> = ({
  data,
  languageUsed
}) => {
  return (
    <div
      className={`max-w-full overflow-auto ${AssistantStyles.markdownContainer}`}
    >
      <ReactMarkdown
        remarkPlugins={[gfm]}
        components={{
          code: props => <CodeBlock {...props} languageUsed={languageUsed} />
        }}
      >
        {data}
      </ReactMarkdown>
    </div>
  );
};

export function formatDateRelativeToToday(dateString: string | undefined) {
  try {
    if (dateString) {
      const currentDate = new Date().getTime();
      const providedDate = new Date(dateString).getTime();
      const timeDifference = currentDate - providedDate;
      const daysDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));

      if (daysDifference === 0) {
        return "Today";
      } else if (daysDifference === 1) {
        return "1 day ago";
      } else if (daysDifference > 1) {
        return `${daysDifference} days ago`;
      } else {
        return "Long time ago";
      }
    } else {
      return "Long time ago";
    }
  } catch (e) {
    console.log(e);
    return "Long time ago";
  }
}

export const checkIfValidLink = (link: string) => {
  const urlRegex = /^(https?):\/\/[^\s/$.?#].[^\s]*$/;
  return urlRegex.test(link);
};
